import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { GST_RATES } from '@tailoring-crm/shared';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';
import { useApiClient } from '../lib/api/api-context.js';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { onboardingKeys, useSetOnboardingStep } from '../lib/onboarding/use-onboarding.js';
import { uploadShopLogo } from '../lib/settings/upload-shop-logo.js';
import {
  useShopDetails,
  useShopLogoUrl,
  useUpdateShopSettings,
} from '../lib/settings/use-shop-settings.js';

/**
 * Who the shop is: its name, how a customer reaches it, and whether its bills carry GST.
 *
 * The two "Getting started" steps that land here — "Your shop" and "GST registration" — are
 * two cards that save separately, for the same reason the presentation screen's cards do:
 * `PATCH /settings` merges what it is sent, so each card carries only its own section.
 *
 * The GST card ticks its checklist step itself when saved. "Not registered" is also the
 * default for a shop that never opened the section, so the checklist cannot infer that answer
 * from the data — saving the card is how the owner gives it.
 */
export function ShopDetailsPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const details = useShopDetails();

  // The API refuses `settings.write` to anybody but the owner; so does this.
  const canWrite = role === 'owner';

  // The GST step links to `#gst`. The router does not scroll to a hash, and the card is not
  // on the page until the settings have loaded, so do it once they have.
  const { hash } = useLocation();
  useEffect(() => {
    if (details.isPending || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [details.isPending, hash]);

  if (details.isPending) return <ListSkeleton rows={5} />;

  return (
    <>
      <PageHeader
        title={t('settings.shopDetails.title')}
        description={t('settings.shopDetails.description')}
      />

      <div className="flex flex-col gap-4">
        <ShopCard canWrite={canWrite} />
        <GstCard canWrite={canWrite} />
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------------------- */

type ShopForm = Record<
  | 'shopName'
  | 'legalName'
  | 'primaryPhone'
  | 'whatsappPhone'
  | 'email'
  | 'line1'
  | 'line2'
  | 'city'
  | 'state'
  | 'pincode',
  string
>;

function useSaveFeedback(): {
  succeeded: () => void;
  failed: (caught: unknown) => void;
} {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return {
    succeeded: () => {
      // The checklist reads its ticks out of these settings; let it see the new ones.
      void queryClient.invalidateQueries({ queryKey: onboardingKeys.all });
      toast({ title: t('settings.shopDetails.saved'), variant: 'success' });
    },
    failed: (caught) =>
      toast({
        title: isApiRequestError(caught) ? caught.message : t('error.unexpected'),
        variant: 'destructive',
      }),
  };
}

const orNull = (value: string): string | null => value.trim() || null;

function ShopCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const details = useShopDetails();
  const update = useUpdateShopSettings();
  const feedback = useSaveFeedback();
  const stored = details.data;

  const [form, setForm] = useState<ShopForm>({
    shopName: '',
    legalName: '',
    primaryPhone: '',
    whatsappPhone: '',
    email: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
  });

  useEffect(() => {
    if (!stored) return;
    setForm({
      shopName: stored.shopName,
      legalName: stored.legalName ?? '',
      primaryPhone: stored.contact.primaryPhone ?? '',
      whatsappPhone: stored.contact.whatsappPhone ?? '',
      email: stored.contact.email ?? '',
      line1: stored.address.line1 ?? '',
      line2: stored.address.line2 ?? '',
      city: stored.address.city ?? '',
      state: stored.address.state ?? '',
      pincode: stored.address.pincode ?? '',
    });
  }, [stored]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await update.mutateAsync({
          shopName: form.shopName.trim(),
          legalName: orNull(form.legalName),
          contact: {
            primaryPhone: orNull(form.primaryPhone),
            whatsappPhone: orNull(form.whatsappPhone),
            email: orNull(form.email),
          },
          address: {
            line1: orNull(form.line1),
            line2: orNull(form.line2),
            city: orNull(form.city),
            state: orNull(form.state),
            pincode: orNull(form.pincode),
          },
        });
        feedback.succeeded();
      } catch (caught) {
        feedback.failed(caught);
      }
    })();
  };

  const field = (
    key: keyof ShopForm,
    options: { type?: string; inputMode?: 'tel' | 'numeric' | 'email' } = {},
  ): ReactElement => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={`shop-${key}`}>{t(`settings.shopDetails.shop.${key}`)}</Label>
      <Input
        id={`shop-${key}`}
        type={options.type ?? 'text'}
        inputMode={options.inputMode}
        value={form[key]}
        disabled={!canWrite}
        onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
      />
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.shopDetails.shop.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('settings.shopDetails.shop.hint')}</p>
          <LogoField canWrite={canWrite} />
          <div className="grid gap-3 sm:grid-cols-2">
            {field('shopName')}
            {field('legalName')}
            {field('primaryPhone', { type: 'tel', inputMode: 'tel' })}
            {field('whatsappPhone', { type: 'tel', inputMode: 'tel' })}
            {field('email', { type: 'email', inputMode: 'email' })}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {field('line1')}
            {field('line2')}
            {field('city')}
            {field('state')}
            {field('pincode', { inputMode: 'numeric' })}
          </div>
          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

function GstCard({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const details = useShopDetails();
  const update = useUpdateShopSettings();
  const setStep = useSetOnboardingStep();
  const feedback = useSaveFeedback();
  const stored = details.data?.gst;

  const [form, setForm] = useState({
    registered: false,
    gstin: '',
    rate: '5',
    pricesIncludeTax: false,
  });

  useEffect(() => {
    if (!stored) return;
    setForm({
      registered: stored.registered ?? false,
      gstin: stored.gstin ?? '',
      rate: String(stored.defaultRatePercent ?? 5),
      pricesIncludeTax: stored.pricesIncludeTax ?? false,
    });
  }, [stored]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    void (async () => {
      try {
        await update.mutateAsync({
          gst: form.registered
            ? {
                registered: true,
                gstin: orNull(form.gstin),
                defaultRatePercent: Number(form.rate),
                pricesIncludeTax: form.pricesIncludeTax,
              }
            : { registered: false, gstin: null },
        });
        // Saved either way is answered either way — see the note at the top of the file.
        await setStep.mutateAsync({ key: 'gst', status: 'completed' });
        feedback.succeeded();
      } catch (caught) {
        feedback.failed(caught);
      }
    })();
  };

  return (
    <Card id="gst">
      <CardHeader>
        <CardTitle>{t('settings.shopDetails.gst.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3" onSubmit={submit}>
          <p className="text-sm text-muted-foreground">{t('settings.shopDetails.gst.hint')}</p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.registered}
              disabled={!canWrite}
              onChange={(event) =>
                setForm((previous) => ({ ...previous, registered: event.target.checked }))
              }
            />
            {t('settings.shopDetails.gst.registered')}
          </label>

          {form.registered ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="gst-gstin">{t('settings.shopDetails.gst.gstin')}</Label>
                <Input
                  id="gst-gstin"
                  value={form.gstin}
                  maxLength={15}
                  required
                  disabled={!canWrite}
                  className="uppercase"
                  onChange={(event) =>
                    setForm((previous) => ({ ...previous, gstin: event.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="gst-rate">{t('settings.shopDetails.gst.rate')}</Label>
                <select
                  id="gst-rate"
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={form.rate}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setForm((previous) => ({ ...previous, rate: event.target.value }))
                  }
                >
                  {GST_RATES.map((rate) => (
                    <option key={rate} value={String(rate)}>
                      {rate}%
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.pricesIncludeTax}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setForm((previous) => ({ ...previous, pricesIncludeTax: event.target.checked }))
                  }
                />
                {t('settings.shopDetails.gst.pricesIncludeTax')}
              </label>
            </div>
          ) : null}

          {canWrite ? (
            <div>
              <Button type="submit" disabled={update.isPending || setStep.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * The logo, which saves the moment it is picked rather than with the card's Save button.
 *
 * The upload IS the decision — nobody picks a file and then wants to think about it — and
 * holding an uploaded image until a Save that never comes would leave it as litter in storage.
 */
function LogoField({ canWrite }: { canWrite: boolean }): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const details = useShopDetails();
  const update = useUpdateShopSettings();
  const feedback = useSaveFeedback();
  const mediaId = details.data?.logo.mediaId ?? null;
  const preview = useShopLogoUrl(mediaId);
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const busy = uploading || update.isPending;

  const pick = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file again after a failure still fires a change.
    event.target.value = '';
    if (!file) return;
    void (async () => {
      setUploading(true);
      try {
        const uploaded = await uploadShopLogo({ client, file });
        await update.mutateAsync({ logo: { mediaId: uploaded } });
        feedback.succeeded();
      } catch (caught) {
        feedback.failed(caught);
      } finally {
        setUploading(false);
      }
    })();
  };

  const remove = (): void => {
    void (async () => {
      try {
        await update.mutateAsync({ logo: { mediaId: null } });
        feedback.succeeded();
      } catch (caught) {
        feedback.failed(caught);
      }
    })();
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
        {mediaId && preview.data ? (
          <img
            src={preview.data}
            alt={t('settings.shopDetails.shop.logoAlt')}
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {t('settings.shopDetails.shop.noLogo')}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium">{t('settings.shopDetails.shop.logo')}</span>
        <span className="text-xs text-muted-foreground">
          {t('settings.shopDetails.shop.logoHint')}
        </span>
        {canWrite ? (
          <div className="flex gap-2">
            <input
              ref={input}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              aria-label={t('settings.shopDetails.shop.logo')}
              onChange={pick}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {uploading
                ? t('settings.shopDetails.shop.logoUploading')
                : mediaId
                  ? t('settings.shopDetails.shop.logoReplace')
                  : t('settings.shopDetails.shop.logoUpload')}
            </Button>
            {mediaId ? (
              <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={remove}>
                {t('settings.shopDetails.shop.logoRemove')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
